import { useEffect, useState } from "react";

interface ResponsiveOptionsConfig {
  lowestThreshold?: number;
  minCards?: number;
  maxCards?: number;
  interval?: number;
}

interface CarouselResponsiveOption {
  breakpoint: string;
  numVisible: number;
  numScroll: number;
}

export const buildResponsiveOptions = ({
  lowestThreshold = 600,
  minCards = 1,
  maxCards = 4,
  interval = 200,
}: ResponsiveOptionsConfig = {}): CarouselResponsiveOption[] => {
  const options: CarouselResponsiveOption[] = [];

  for (let numVisible = minCards; numVisible <= maxCards; numVisible++) {
    options.push({
      breakpoint: `${lowestThreshold + (numVisible - minCards) * interval}px`,
      numVisible: numVisible,
      numScroll: numVisible,
    });
  }

  return options;
};

const computeNumVisible = ({
  lowestThreshold,
  minCards,
  maxCards,
  interval,
}: Required<ResponsiveOptionsConfig>): number => {
  if (typeof window === "undefined") return maxCards;

  const responsiveOptions = buildResponsiveOptions({
    lowestThreshold,
    minCards,
    maxCards,
    interval,
  });

  // Mirrors PrimeReact: each breakpoint is a max-width; pick the first
  // breakpoint the viewport fits under. If none match, fall back to maxCards.
  for (const option of responsiveOptions) {
    const breakpoint = Number.parseInt(option.breakpoint, 10);
    if (!Number.isNaN(breakpoint) && window.innerWidth <= breakpoint) {
      return option.numVisible;
    }
  }

  return maxCards;
};

export const useCurrentNumVisible = ({
  lowestThreshold = 600,
  minCards = 1,
  maxCards = 4,
  interval = 200,
}: ResponsiveOptionsConfig = {}): number => {
  const getNumVisible = () => computeNumVisible({ lowestThreshold, minCards, maxCards, interval });

  const [numVisible, setNumVisible] = useState<number>(getNumVisible);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onResize = () => setNumVisible(getNumVisible());
    setNumVisible(getNumVisible());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [lowestThreshold, minCards, maxCards, interval]);

  return numVisible;
};

// Hook to determine if the carousel is ready to be displayed without causing a resize "blip".
export const useCarouselReady = (isLoading = false): boolean => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isLoading) {
      setIsReady(false);
      return;
    }

    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setIsReady(true));
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [isLoading]);

  return isReady;
};
